import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Barcode, ShoppingCart, Package, Plus, Trash2, Lock, Unlock, AlertTriangle, ImageIcon, TrendingUp, DollarSign, Banknote, CreditCard as CardIcon, Smartphone } from "lucide-react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, Legend } from "recharts";
import { PageHeader } from "@/components/PageHeader";
import { useAuth } from "@/lib/auth";

const METHOD_COLORS = { cash: "#10B981", card: "#3B82F6", pix: "#E11D48", other: "#F59E0B" };
const METHOD_LABELS = { cash: "Dinheiro", card: "Cartão", pix: "Pix", other: "Outro" };

export default function Shop() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("pdv");
  const [products, setProducts] = useState([]);
  const [session, setSession] = useState(null);
  const [history, setHistory] = useState([]);
  const [salesToday, setSalesToday] = useState([]);
  const [report, setReport] = useState(null);
  const [reportDays, setReportDays] = useState(30);
  // POS state
  const [cart, setCart] = useState([]);
  const [scan, setScan] = useState("");
  const [payment, setPayment] = useState("cash");
  const scanRef = useRef(null);
  // Dialogs
  const [openOpen, setOpenOpen] = useState(false);
  const [openForm, setOpenForm] = useState({ opening_amount: "0", notes: "" });
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeForm, setCloseForm] = useState({ counted_amount: "", notes: "" });
  const [prodOpen, setProdOpen] = useState(false);
  const [prodForm, setProdForm] = useState(emptyProduct());
  const [editing, setEditing] = useState(null);
  const [units, setUnits] = useState([]);

  function emptyProduct() {
    return { sku: "", name: "", category: "Suplementos", price: 0, cost: 0, min_stock: 5, image: null, all_units: true, unit_ids: [], stock_by_unit: {} };
  }

  const loadAll = async () => {
    const [p, s, h, u] = await Promise.all([
      api.get("/products"),
      api.get("/cash/current").catch(() => ({ data: null })),
      api.get("/cash/history?limit=15"),
      isAdmin ? api.get("/units").catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
    ]);
    setProducts(p.data); setSession(s.data); setHistory(h.data); setUnits(u.data || []);
    if (s.data?.id) {
      const sales = await api.get(`/sales?session_id=${s.data.id}`);
      setSalesToday(sales.data);
    } else setSalesToday([]);
  };
  const loadReport = async (d) => {
    try {
      const { data } = await api.get(`/reports/sales?days=${d || reportDays}`);
      setReport(data);
    } catch { toast.error("Erro no relatório"); }
  };
  useEffect(() => { loadAll(); }, []);
  useEffect(() => { if (tab === "pdv" && session && scanRef.current) scanRef.current.focus(); }, [tab, session]);
  useEffect(() => { if (tab === "report" && isAdmin) loadReport(reportDays); }, [tab, reportDays, isAdmin]);
  useEffect(() => { if (!isAdmin && (tab === "report" || tab === "history")) setTab("pdv"); }, [isAdmin, tab]);

  const openCash = async () => {
    try {
      await api.post("/cash/open", { opening_amount: parseFloat(openForm.opening_amount || "0"), notes: openForm.notes });
      toast.success("Caixa aberto"); setOpenOpen(false); setOpenForm({ opening_amount: "0", notes: "" }); loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const closeCash = async () => {
    if (!session) return;
    try {
      const { data } = await api.post(`/cash/close/${session.id}`, {
        counted_amount: parseFloat(closeForm.counted_amount || "0"), notes: closeForm.notes,
      });
      toast.success(`Caixa fechado. Diferença: R$ ${data.difference.toFixed(2)}`);
      setCloseOpen(false); setCloseForm({ counted_amount: "", notes: "" }); loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const handleScan = async (e) => {
    e.preventDefault();
    if (!scan.trim()) return;
    try {
      const { data: prod } = await api.get(`/products/by-sku/${encodeURIComponent(scan.trim())}`);
      addToCart(prod); setScan(""); scanRef.current?.focus();
    } catch { toast.error("Código não encontrado"); setScan(""); }
  };

  const addToCart = (prod) => {
    const existing = cart.find(c => c.product_id === prod.id);
    if (existing) {
      setCart(cart.map(c => c.product_id === prod.id ? { ...c, qty: c.qty + 1, subtotal: (c.qty + 1) * c.price } : c));
    } else {
      setCart([...cart, { product_id: prod.id, sku: prod.sku, name: prod.name, price: prod.price, qty: 1, subtotal: prod.price, image: prod.image }]);
    }
  };
  const changeQty = (idx, qty) => {
    const q = Math.max(1, parseInt(qty) || 1);
    setCart(cart.map((c, i) => i === idx ? { ...c, qty: q, subtotal: q * c.price } : c));
  };
  const removeFromCart = (idx) => setCart(cart.filter((_, i) => i !== idx));
  const total = cart.reduce((s, c) => s + c.subtotal, 0);

  const finalizeSale = async () => {
    if (cart.length === 0) return toast.error("Carrinho vazio");
    try {
      const items = cart.map(({ image, ...rest }) => rest);
      await api.post("/sales", { items, payment_method: payment });
      toast.success(`Venda finalizada — R$ ${total.toFixed(2)}`);
      setCart([]); setPayment("cash"); loadAll();
      scanRef.current?.focus();
    } catch (e) {
      if (e.response?.status === 423 && e.response?.data?.detail?.code === "shop_cash_closed") {
        const reason = window.prompt(
          "⚠️ CAIXA DA LOJA FECHADO\n\nPara registrar esta venda, é preciso reabrir o caixa da Loja/PDV.\nInforme o MOTIVO (ficará no histórico de auditoria):",
          ""
        );
        if (reason === null) return;
        const r = (reason || "").trim();
        if (!r) return toast.error("Motivo obrigatório");
        try {
          await api.post(`/cash/reopen-session?reason=${encodeURIComponent(r)}`);
          toast.success("Caixa reaberto — refazendo venda...");
          return finalizeSale();
        } catch (err) {
          toast.error(err.response?.data?.detail || "Falha ao reabrir caixa");
        }
      } else {
        toast.error(e.response?.data?.detail || "Erro");
      }
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) return toast.error("Imagem muito grande (máx 3MB)");
    // Downscale to 400px width for storage efficiency
    const img = new Image();
    const reader = new FileReader();
    reader.onload = () => {
      img.onload = () => {
        const scale = Math.min(1, 400 / img.width);
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        setProdForm(prev => ({ ...prev, image: canvas.toDataURL("image/jpeg", 0.8) }));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const saveProduct = async () => {
    try {
      // Monta stock_by_unit a partir das unidades selecionadas
      const scope = prodForm.all_units ? units.map(u => u.id) : (prodForm.unit_ids || []);
      const sbu = {};
      scope.forEach(uid => {
        const v = parseInt(prodForm.stock_by_unit?.[uid] ?? 0);
        sbu[uid] = isNaN(v) ? 0 : Math.max(0, v);
      });
      const body = {
        ...prodForm,
        price: parseFloat(prodForm.price),
        cost: parseFloat(prodForm.cost || 0),
        min_stock: parseInt(prodForm.min_stock || 5),
        stock_by_unit: sbu,
        unit_ids: prodForm.all_units ? [] : scope,
      };
      // remove campo legado
      delete body.stock;
      if (editing) await api.patch(`/products/${editing.id}`, body);
      else await api.post("/products", body);
      toast.success("Salvo");
      setProdOpen(false); setEditing(null); setProdForm(emptyProduct()); loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const editProduct = (p) => {
    setEditing(p);
    setProdForm({
      sku: p.sku, name: p.name, category: p.category, price: p.price, cost: p.cost,
      min_stock: p.min_stock, image: p.image || null,
      all_units: p.all_units !== false,
      unit_ids: p.unit_ids || [],
      stock_by_unit: p.stock_by_unit || {},
    });
    setProdOpen(true);
  };
  const removeProduct = async (id) => {
    if (!window.confirm("Remover produto?")) return;
    await api.delete(`/products/${id}`); toast.success("Removido"); loadAll();
  };

  const lowStock = products.filter(p => p.active && p.stock <= p.min_stock);
  const expected = session ? (session.opening_amount + session.total_cash).toFixed(2) : "0.00";

  // Today method breakdown (from current session)
  const todayByMethod = session ? [
    { method: "cash", label: METHOD_LABELS.cash, total: session.total_cash || 0, icon: Banknote },
    { method: "card", label: METHOD_LABELS.card, total: session.total_card || 0, icon: CardIcon },
    { method: "pix", label: METHOD_LABELS.pix, total: session.total_pix || 0, icon: Smartphone },
  ] : [];

  return (
    <div className="space-y-4">
      <PageHeader
        title="Loja / PDV"
        subtitle="Venda de suplementos, água, acessórios · caixa diário independente"
        actions={
          session ? (
            <>
              <span className="text-xs px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 font-semibold border border-emerald-200" data-testid="cash-status-badge">
                <Unlock className="w-3 h-3 inline mr-1"/>Caixa aberto · {session.opened_by_name}
              </span>
              <Button variant="outline" onClick={()=>setCloseOpen(true)} data-testid="cash-close-button" className="border-rose-600 text-rose-600 hover:bg-rose-50 font-bold uppercase">
                <Lock className="w-4 h-4 mr-2"/>Fechar caixa
              </Button>
            </>
          ) : (
            <Button onClick={()=>setOpenOpen(true)} data-testid="cash-open-button" className="bg-emerald-600 hover:bg-emerald-700 font-bold uppercase">
              <Unlock className="w-4 h-4 mr-2"/>Abrir caixa
            </Button>
          )
        }
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="pdv" data-testid="tab-pdv">PDV</TabsTrigger>
          <TabsTrigger value="products" data-testid="tab-products">Produtos {lowStock.length > 0 && <span className="ml-1 text-amber-600">⚠</span>}</TabsTrigger>
          {isAdmin && <TabsTrigger value="report" data-testid="tab-report">Relatórios</TabsTrigger>}
          {isAdmin && <TabsTrigger value="history" data-testid="tab-history">Histórico caixa</TabsTrigger>}
        </TabsList>

        <TabsContent value="pdv" className="mt-4">
          {session && (
            <div className="grid grid-cols-3 gap-3 mb-4">
              {todayByMethod.map(m => {
                const Icon = m.icon;
                return (
                  <Card key={m.method} className="p-4 border border-slate-200" data-testid={`today-${m.method}-metric`}>
                    <div className="flex items-center gap-2">
                      <Icon className="w-5 h-5" style={{ color: METHOD_COLORS[m.method] }}/>
                      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{m.label} hoje</div>
                    </div>
                    <div className="font-heading text-2xl font-bold text-slate-900 mt-1">R$ {m.total.toFixed(2)}</div>
                  </Card>
                );
              })}
            </div>
          )}
          {!session ? (
            <Card className="p-12 text-center border border-slate-200">
              <Lock className="w-14 h-14 text-slate-300 mx-auto mb-3"/>
              <h3 className="font-heading text-xl font-bold text-slate-900">Caixa fechado</h3>
              <p className="text-slate-500 text-sm mt-1">Abra o caixa acima antes de vender.</p>
            </Card>
          ) : (
            <div className="grid lg:grid-cols-3 gap-4">
              <Card className="lg:col-span-2 p-5 border border-slate-200">
                <form onSubmit={handleScan} className="mb-4">
                  <Label className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-2 mb-2">
                    <Barcode className="w-4 h-4"/>Leitor de código de barras
                  </Label>
                  <Input ref={scanRef} value={scan} onChange={(e)=>setScan(e.target.value)}
                    data-testid="pos-barcode-input"
                    placeholder="Aponte o leitor ou digite o código e pressione Enter"
                    className="font-mono text-lg py-6"
                    autoFocus/>
                </form>

                <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Ou toque nos produtos</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4 max-h-56 overflow-y-auto">
                  {products.filter(p => p.active && p.stock > 0).map(p => (
                    <button key={p.id} onClick={()=>addToCart(p)} data-testid="pos-product-card"
                      className="text-left p-2 bg-slate-50 hover:bg-rose-50 hover:border-rose-300 border border-slate-200 rounded-lg transition-colors">
                      <div className="aspect-square w-full bg-white rounded mb-1 overflow-hidden flex items-center justify-center">
                        {p.image ? <img src={p.image} alt="" className="w-full h-full object-cover"/> : <Package className="w-8 h-8 text-slate-300"/>}
                      </div>
                      <div className="text-xs font-semibold text-slate-900 truncate">{p.name}</div>
                      <div className="text-xs text-rose-600 font-mono">R$ {p.price.toFixed(2)}</div>
                      <div className="text-[10px] text-slate-500">{p.stock} un</div>
                    </button>
                  ))}
                  {products.filter(p => p.active && p.stock > 0).length === 0 && <p className="text-xs text-slate-500 col-span-full">Sem produtos com estoque</p>}
                </div>

                <div className="border-t border-slate-200 pt-3">
                  <div className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2"><ShoppingCart className="w-4 h-4"/>Carrinho ({cart.length})</div>
                  {cart.length === 0 && <p className="text-sm text-slate-500 text-center py-8">Escaneie um produto ou toque acima</p>}
                  {cart.map((c, i) => (
                    <div key={i} className="flex items-center gap-2 py-2 border-b border-slate-100" data-testid="pos-cart-item">
                      {c.image ? <img src={c.image} className="w-10 h-10 rounded object-cover" alt=""/> : <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center"><Package className="w-5 h-5 text-slate-400"/></div>}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">{c.name}</div>
                        <div className="text-xs text-slate-500 font-mono">R$ {c.price.toFixed(2)}</div>
                      </div>
                      <Input type="number" min={1} value={c.qty} onChange={(e)=>changeQty(i, e.target.value)} className="w-16 h-8"/>
                      <div className="text-sm font-mono font-bold w-20 text-right">R$ {c.subtotal.toFixed(2)}</div>
                      <Button size="icon" variant="ghost" onClick={()=>removeFromCart(i)}><Trash2 className="w-4 h-4 text-slate-400"/></Button>
                    </div>
                  ))}
                </div>
              </Card>

              <Card className="p-5 border border-slate-200 bg-gradient-to-br from-slate-50 to-white h-fit">
                <div className="text-xs uppercase tracking-wider text-slate-500">Total</div>
                <div className="font-heading text-4xl font-extrabold text-slate-900 mb-4" data-testid="pos-total">R$ {total.toFixed(2)}</div>
                <Label>Forma de pagamento</Label>
                <Select value={payment} onValueChange={setPayment}>
                  <SelectTrigger data-testid="pos-payment-select" className="mb-4"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">💵 Dinheiro</SelectItem>
                    <SelectItem value="card">💳 Cartão</SelectItem>
                    <SelectItem value="pix">📱 Pix</SelectItem>
                    <SelectItem value="other">Outro</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={finalizeSale} disabled={cart.length === 0} data-testid="pos-finalize-button" className="w-full bg-rose-600 hover:bg-rose-700 text-lg py-6">
                  Finalizar venda
                </Button>
                <div className="mt-6 pt-4 border-t border-slate-200 space-y-2 text-sm">
                  <div className="flex justify-between text-slate-600"><span>Vendas hoje</span><span className="font-mono">{salesToday.length}</span></div>
                  <div className="flex justify-between font-bold text-slate-900 pt-2 border-t border-slate-200">
                    <span>Total sessão</span>
                    <span className="font-mono">R$ {((session.total_cash || 0) + (session.total_card || 0) + (session.total_pix || 0) + (session.total_other || 0)).toFixed(2)}</span>
                  </div>
                </div>
              </Card>
            </div>
          )}
        </TabsContent>

        <TabsContent value="products" className="mt-4 space-y-4">
          <div className="flex justify-between items-center">
            {lowStock.length > 0 && (
              <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-lg">
                <AlertTriangle className="w-4 h-4"/>{lowStock.length} produto(s) com estoque baixo
              </div>
            )}
            {isAdmin && (
              <Dialog open={prodOpen} onOpenChange={(v)=>{setProdOpen(v); if(!v){setEditing(null); setProdForm(emptyProduct());}}}>
                <DialogTrigger asChild>
                  <Button data-testid="product-new-button" className="bg-rose-600 hover:bg-rose-700 ml-auto"><Plus className="w-4 h-4 mr-2"/>Novo produto</Button>
                </DialogTrigger>
                <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>{editing ? "Editar" : "Novo"} produto</DialogTitle></DialogHeader>
                <div className="grid grid-cols-[120px_1fr] gap-4 mt-4">
                  <div>
                    <Label>Foto</Label>
                    <div className="mt-1 aspect-square w-full bg-slate-100 rounded-lg overflow-hidden flex items-center justify-center border border-slate-200">
                      {prodForm.image ? <img src={prodForm.image} alt="" className="w-full h-full object-cover"/> : <ImageIcon className="w-8 h-8 text-slate-400"/>}
                    </div>
                    <label className="mt-2 block cursor-pointer text-xs text-center bg-slate-100 hover:bg-slate-200 rounded-lg py-1.5 font-semibold text-slate-700">
                      {prodForm.image ? "Trocar" : "Carregar"}
                      <input type="file" accept="image/*" className="hidden" onChange={handleImageChange} data-testid="product-image-input"/>
                    </label>
                    {prodForm.image && <button onClick={()=>setProdForm({...prodForm,image:null})} className="mt-1 w-full text-xs text-rose-600 hover:underline">Remover foto</button>}
                  </div>
                  <div className="space-y-3">
                    <div><Label>Código de barras (SKU)</Label><Input value={prodForm.sku} onChange={(e)=>setProdForm({...prodForm,sku:e.target.value})} data-testid="product-sku-input" className="font-mono" placeholder="7891234567890"/></div>
                    <div><Label>Nome</Label><Input value={prodForm.name} onChange={(e)=>setProdForm({...prodForm,name:e.target.value})} data-testid="product-name-input"/></div>
                    <div><Label>Categoria</Label><Input value={prodForm.category} onChange={(e)=>setProdForm({...prodForm,category:e.target.value})} placeholder="Suplementos, Bebidas, Acessórios"/></div>
                    <div className="grid grid-cols-2 gap-2">
                      <div><Label>Preço (R$)</Label><Input type="number" step="0.01" value={prodForm.price} onChange={(e)=>setProdForm({...prodForm,price:e.target.value})} data-testid="product-price-input"/></div>
                      <div><Label>Custo (R$)</Label><Input type="number" step="0.01" value={prodForm.cost} onChange={(e)=>setProdForm({...prodForm,cost:e.target.value})}/></div>
                      <div><Label>Estoque mínimo (alerta)</Label><Input type="number" value={prodForm.min_stock} onChange={(e)=>setProdForm({...prodForm,min_stock:e.target.value})}/></div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 p-3 rounded-lg border-2 border-sky-200 bg-sky-50/40 space-y-3" data-testid="product-units-section">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <Label className="font-heading uppercase tracking-wide text-slate-800 text-xs">Disponibilidade e estoque por unidade</Label>
                      <p className="text-[11px] text-slate-500">Admin controla em quais unidades o produto aparece e quanto cada uma tem em estoque.</p>
                    </div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 cursor-pointer" data-testid="product-all-units-toggle">
                      <input type="checkbox" checked={!!prodForm.all_units} onChange={(e)=>setProdForm({...prodForm, all_units: e.target.checked, unit_ids: e.target.checked ? [] : prodForm.unit_ids})} className="w-4 h-4"/>
                      Disponível em todas as unidades
                    </label>
                  </div>

                  {!prodForm.all_units && (
                    <div className="grid grid-cols-2 gap-1.5" data-testid="product-units-select">
                      {units.map(u => {
                        const checked = (prodForm.unit_ids || []).includes(u.id);
                        return (
                          <label key={u.id} className={`flex items-center gap-2 text-sm px-2 py-1.5 rounded border cursor-pointer ${checked ? "bg-emerald-50 border-emerald-300" : "bg-white border-slate-200"}`}>
                            <input type="checkbox" checked={checked} onChange={(e)=>{
                              const next = e.target.checked
                                ? [...(prodForm.unit_ids || []), u.id]
                                : (prodForm.unit_ids || []).filter(x => x !== u.id);
                              setProdForm({...prodForm, unit_ids: next});
                            }}/>
                            {u.name}
                          </label>
                        );
                      })}
                    </div>
                  )}

                  <div className="space-y-1.5" data-testid="product-stock-per-unit">
                    <div className="text-xs font-bold text-slate-600 uppercase tracking-wide">Estoque por unidade</div>
                    {(prodForm.all_units ? units : units.filter(u => (prodForm.unit_ids || []).includes(u.id))).map(u => (
                      <div key={u.id} className="flex items-center gap-2">
                        <span className="text-sm text-slate-700 flex-1">{u.name}</span>
                        <Input type="number" min={0} className="w-28 font-mono"
                          value={prodForm.stock_by_unit?.[u.id] ?? 0}
                          onChange={(e)=>setProdForm({...prodForm, stock_by_unit: { ...(prodForm.stock_by_unit || {}), [u.id]: e.target.value }})}
                          data-testid={`product-stock-input-${u.id}`}/>
                        <span className="text-xs text-slate-500 w-6">un</span>
                      </div>
                    ))}
                    {!prodForm.all_units && (prodForm.unit_ids || []).length === 0 && (
                      <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">Selecione pelo menos uma unidade acima.</div>
                    )}
                  </div>
                </div>
                <Button onClick={saveProduct} data-testid="product-save-button" className="mt-4 w-full bg-rose-600 hover:bg-rose-700">Salvar</Button>
              </DialogContent>
            </Dialog>
            )}
          </div>

          <Card className="border border-slate-200 overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3">Produto</th><th className="px-4 py-3">SKU</th><th className="px-4 py-3">Categoria</th>
                  {isAdmin && <th className="px-4 py-3 text-right">Custo</th>}
                  <th className="px-4 py-3 text-right">Preço</th>
                  {isAdmin && <th className="px-4 py-3 text-right">Margem</th>}
                  <th className="px-4 py-3">Unidades</th>
                  <th className="px-4 py-3 text-right">Estoque</th><th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody data-testid="products-list">
                {products.map(p => {
                  const margin = p.cost > 0 ? ((p.price - p.cost) / p.price * 100) : 0;
                  const sbu = p.stock_by_unit || {};
                  const totalStock = isAdmin ? Object.values(sbu).reduce((a,b)=>a + (parseInt(b)||0), 0) : (p.stock || 0);
                  const unitsLabel = p.all_units !== false
                    ? "Todas"
                    : (p.unit_ids || []).map(uid => units.find(u=>u.id===uid)?.name).filter(Boolean).join(", ") || "—";
                  return (
                    <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {p.image ? <img src={p.image} className="w-10 h-10 rounded object-cover border border-slate-200" alt=""/> : <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center border border-slate-200"><Package className="w-5 h-5 text-slate-400"/></div>}
                          <span className="font-medium text-slate-900">{p.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">{p.sku}</td>
                      <td className="px-4 py-3 text-slate-600 text-sm">{p.category}</td>
                      {isAdmin && <td className="px-4 py-3 text-right font-mono text-sm text-slate-600">R$ {p.cost.toFixed(2)}</td>}
                      <td className="px-4 py-3 text-right font-mono font-bold text-rose-600">R$ {p.price.toFixed(2)}</td>
                      {isAdmin && <td className="px-4 py-3 text-right"><span className={`text-xs px-2 py-1 rounded-full font-semibold ${margin >= 30 ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{margin.toFixed(0)}%</span></td>}
                      <td className="px-4 py-3 text-xs text-slate-600 max-w-[180px]">
                        {p.all_units !== false ? (
                          <span className="inline-flex items-center gap-1 text-sky-700 bg-sky-50 border border-sky-200 px-2 py-0.5 rounded-full font-semibold">Todas</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {(p.unit_ids || []).map(uid => {
                              const nm = units.find(u=>u.id===uid)?.name;
                              return nm ? <span key={uid} className="bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">{nm}</span> : null;
                            })}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {isAdmin ? (
                          <div className="text-xs text-right">
                            <div className={`inline-block px-2 py-1 rounded-full font-semibold ${totalStock <= (p.min_stock || 0) ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`} title={unitsLabel}>{totalStock} total</div>
                            {Object.keys(sbu).length > 0 && (
                              <div className="text-[10px] text-slate-500 mt-1 space-y-0.5">
                                {Object.entries(sbu).map(([uid, qty]) => {
                                  const nm = units.find(u=>u.id===uid)?.name || "?";
                                  return <div key={uid}><strong>{nm}</strong>: {qty}</div>;
                                })}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className={`text-xs px-2 py-1 rounded-full font-semibold ${totalStock <= (p.min_stock || 0) ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}>{totalStock}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {isAdmin && <Button variant="ghost" size="sm" onClick={()=>editProduct(p)}>Editar</Button>}
                        {isAdmin && <Button variant="ghost" size="icon" onClick={()=>removeProduct(p.id)}><Trash2 className="w-4 h-4 text-slate-400"/></Button>}
                      </td>
                    </tr>
                  );
                })}
                {products.length === 0 && <tr><td colSpan={9} className="px-4 py-12 text-center text-slate-500">Nenhum produto cadastrado</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="report" className="mt-4 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="font-heading text-xl font-bold text-slate-900">Relatório de vendas</h3>
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase tracking-wider text-slate-500">Período</span>
              <select value={reportDays} onChange={(e)=>setReportDays(parseInt(e.target.value))}
                data-testid="report-period-select"
                className="text-sm px-3 py-1.5 border border-slate-300 rounded-lg bg-white">
                <option value={7}>7 dias</option>
                <option value={30}>30 dias</option>
                <option value={90}>90 dias</option>
                <option value={365}>1 ano</option>
              </select>
            </div>
          </div>

          {!report && <p className="text-slate-500 text-center py-8">Carregando...</p>}
          {report && (
            <>
              {/* KPI blocks */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  { label: "Vendas hoje", value: report.today.count, sub: `R$ ${report.today.revenue.toFixed(2)}`, testid: "report-today" },
                  { label: "Últimos 7 dias", value: `R$ ${report.week.revenue.toFixed(2)}`, sub: `${report.week.count} vendas`, testid: "report-week" },
                  { label: `Receita ${reportDays}d`, value: `R$ ${report.period.revenue.toFixed(2)}`, sub: `${report.period.count} vendas`, testid: "report-revenue" },
                  { label: `Lucro ${reportDays}d`, value: `R$ ${report.period.profit.toFixed(2)}`, sub: `custo R$ ${report.period.cost.toFixed(2)}`, testid: "report-profit" },
                ].map(k => (
                  <Card key={k.label} className="p-4 border border-slate-200" data-testid={k.testid}>
                    <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{k.label}</div>
                    <div className="font-heading text-2xl font-bold text-slate-900 mt-1">{k.value}</div>
                    <div className="text-xs text-slate-500 mt-1">{k.sub}</div>
                  </Card>
                ))}
              </div>

              {/* Payment method breakdown */}
              <Card className="p-5 border border-slate-200">
                <h4 className="font-heading font-bold text-slate-900 mb-4">Vendas por forma de pagamento</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  {["cash", "card", "pix", "other"].map(m => (
                    <div key={m} className="p-3 rounded-lg border" style={{ borderColor: METHOD_COLORS[m] + "33", background: METHOD_COLORS[m] + "0d" }} data-testid={`report-method-${m}`}>
                      <div className="text-xs uppercase tracking-wider font-semibold" style={{ color: METHOD_COLORS[m] }}>{METHOD_LABELS[m]}</div>
                      <div className="font-heading text-xl font-bold text-slate-900 mt-1">R$ {report.period.by_method[m].toFixed(2)}</div>
                    </div>
                  ))}
                </div>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={Object.entries(report.period.by_method).map(([k, v]) => ({ method: METHOD_LABELS[k], total: v, key: k }))}>
                      <XAxis dataKey="method" tick={{ fontSize: 12 }}/>
                      <YAxis tick={{ fontSize: 11 }}/>
                      <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} formatter={(v)=>`R$ ${v.toFixed(2)}`}/>
                      <Bar dataKey="total" radius={[6,6,0,0]}>
                        {Object.entries(report.period.by_method).map(([k], i) => <Cell key={i} fill={METHOD_COLORS[k]}/>)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              {/* Daily series */}
              <Card className="p-5 border border-slate-200">
                <h4 className="font-heading font-bold text-slate-900 mb-4">Receita e lucro por dia</h4>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={report.daily}>
                      <XAxis dataKey="day" tick={{ fontSize: 10 }}/>
                      <YAxis tick={{ fontSize: 11 }}/>
                      <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} formatter={(v)=>`R$ ${v.toFixed(2)}`}/>
                      <Legend wrapperStyle={{ fontSize: 12 }}/>
                      <Line type="monotone" dataKey="revenue" name="Receita" stroke="#E11D48" strokeWidth={2} dot={{ r: 3 }}/>
                      <Line type="monotone" dataKey="profit" name="Lucro" stroke="#10B981" strokeWidth={2} dot={{ r: 3 }}/>
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              {/* Top products */}
              <Card className="p-5 border border-slate-200">
                <h4 className="font-heading font-bold text-slate-900 mb-4 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-rose-600"/>Produtos mais vendidos</h4>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[600px]">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                        <th className="py-2 px-3">#</th><th className="py-2 px-3">Produto</th>
                        <th className="py-2 px-3 text-right">Qtd</th><th className="py-2 px-3 text-right">Receita</th>
                        <th className="py-2 px-3 text-right">Lucro</th><th className="py-2 px-3 text-right">Margem</th>
                      </tr>
                    </thead>
                    <tbody data-testid="report-top-products">
                      {report.top_products.map((t, i) => {
                        const margin = t.revenue > 0 ? (t.profit / t.revenue * 100) : 0;
                        return (
                          <tr key={t.product_id} className="border-b border-slate-100">
                            <td className="py-2 px-3 font-heading font-bold text-slate-400">#{i + 1}</td>
                            <td className="py-2 px-3">
                              <div className="flex items-center gap-2">
                                {t.image ? <img src={t.image} className="w-9 h-9 rounded object-cover" alt=""/> : <div className="w-9 h-9 bg-slate-100 rounded flex items-center justify-center"><Package className="w-4 h-4 text-slate-400"/></div>}
                                <span className="font-medium text-slate-900">{t.name}</span>
                              </div>
                            </td>
                            <td className="py-2 px-3 text-right font-mono font-bold">{t.qty}</td>
                            <td className="py-2 px-3 text-right font-mono">R$ {t.revenue.toFixed(2)}</td>
                            <td className="py-2 px-3 text-right font-mono text-emerald-600 font-bold">R$ {t.profit.toFixed(2)}</td>
                            <td className="py-2 px-3 text-right"><span className="text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full font-semibold">{margin.toFixed(0)}%</span></td>
                          </tr>
                        );
                      })}
                      {report.top_products.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-slate-500">Sem vendas no período</td></tr>}
                    </tbody>
                  </table>
                </div>
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <Card className="border border-slate-200 overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3">Abertura</th><th className="px-4 py-3">Operador</th><th className="px-4 py-3 text-right">Inicial</th>
                  <th className="px-4 py-3 text-right">Dinheiro</th><th className="px-4 py-3 text-right">Cartão</th><th className="px-4 py-3 text-right">Pix</th>
                  <th className="px-4 py-3 text-right">Contado</th><th className="px-4 py-3 text-right">Diferença</th><th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody data-testid="cash-history-list">
                {history.map(h => (
                  <tr key={h.id} className="border-b border-slate-100">
                    <td className="px-4 py-3 text-sm">{new Date(h.opened_at).toLocaleString("pt-BR")}</td>
                    <td className="px-4 py-3 text-sm">{h.opened_by_name}</td>
                    <td className="px-4 py-3 text-right font-mono text-sm">R$ {(h.opening_amount||0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-right font-mono text-sm">R$ {(h.total_cash||0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-right font-mono text-sm">R$ {(h.total_card||0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-right font-mono text-sm">R$ {(h.total_pix||0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-right font-mono text-sm">{h.counted_amount != null ? `R$ ${h.counted_amount.toFixed(2)}` : "—"}</td>
                    <td className={`px-4 py-3 text-right font-mono text-sm font-bold ${h.difference == null ? "" : h.difference === 0 ? "text-emerald-600" : h.difference > 0 ? "text-emerald-600" : "text-rose-600"}`}>
                      {h.difference != null ? `R$ ${h.difference.toFixed(2)}` : "—"}
                    </td>
                    <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full font-semibold ${h.status === "open" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{h.status === "open" ? "Aberto" : "Fechado"}</span></td>
                  </tr>
                ))}
                {history.length === 0 && <tr><td colSpan={9} className="px-4 py-12 text-center text-slate-500">Nenhuma sessão de caixa</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={openOpen} onOpenChange={setOpenOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Abrir caixa</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-4">
            <div><Label>Fundo inicial (troco) R$</Label><Input type="number" step="0.01" value={openForm.opening_amount} onChange={(e)=>setOpenForm({...openForm,opening_amount:e.target.value})} data-testid="cash-open-amount-input"/></div>
            <div><Label>Observações</Label><Textarea rows={2} value={openForm.notes} onChange={(e)=>setOpenForm({...openForm,notes:e.target.value})}/></div>
            <Button onClick={openCash} data-testid="cash-open-confirm-button" className="w-full bg-emerald-600 hover:bg-emerald-700">Confirmar abertura</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={closeOpen} onOpenChange={setCloseOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Fechar caixa</DialogTitle></DialogHeader>
          {session && (
            <div className="space-y-3 mt-4">
              <div className="p-3 bg-slate-50 rounded-lg space-y-1 text-sm">
                <div className="flex justify-between"><span>Fundo inicial:</span><span className="font-mono">R$ {session.opening_amount.toFixed(2)}</span></div>
                <div className="flex justify-between"><span>+ Vendas em dinheiro:</span><span className="font-mono">R$ {(session.total_cash || 0).toFixed(2)}</span></div>
                <div className="flex justify-between font-bold pt-2 border-t border-slate-200"><span>Esperado no caixa:</span><span className="font-mono">R$ {expected}</span></div>
              </div>
              <div><Label>Valor contado no caixa R$</Label><Input type="number" step="0.01" value={closeForm.counted_amount} onChange={(e)=>setCloseForm({...closeForm,counted_amount:e.target.value})} data-testid="cash-close-amount-input" placeholder={expected}/></div>
              <div><Label>Observações</Label><Textarea rows={2} value={closeForm.notes} onChange={(e)=>setCloseForm({...closeForm,notes:e.target.value})}/></div>
              <Button onClick={closeCash} data-testid="cash-close-confirm-button" className="w-full bg-rose-600 hover:bg-rose-700">Fechar caixa</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
